'use client';

import React, { memo } from 'react';

import Image from 'next/image';

import { getDefaultTrustLogos } from '@/components/sections/BaseHeroSection/BaseHeroSection';
import { useManifest } from '@/lib/manifest-context';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  ClinicDoctifyWidget,
  ClinicTrustPilotWidget,
} from '@/components/widgets/ClinicDoctifyWidget';
import RichText from '@/components/ui/RichText/RichText';

interface RateYourExperienceSectionProps {
  data?: any; // Strictly typed in your production environment
}

const RateYourExperienceSection: React.FC<RateYourExperienceSectionProps> = ({
  data,
}) => {
  const manifest = useManifest();
  const { ref, sectionClasses, childClasses, getChildStyle } =
    useScrollAnimation();

  if (!data) return null;

  return (
    <section
      ref={ref}
      className={`bg-surface-light overflow-hidden py-8 md:py-20 ${sectionClasses}`}
      data-testid="rate-experience-section"
    >
      <div className="content-wrapper">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
          {/* Text Content */}
          <div className="space-y-6">
            <div className={childClasses()} style={getChildStyle(200)}>
              <RichText
                styles={{
                  p: 'text-3xl md:text-4xl lg:text-5xl font-bold text-theme-brand-blue leading-tight',
                }}
              >
                {data.heading}
              </RichText>
            </div>

            <div
              className={`prose prose-lg max-w-none leading-relaxed text-gray-600 ${childClasses()}`}
              style={getChildStyle(400)}
            >
              <RichText>{data.description}</RichText>
            </div>
          </div>

          {/* Review Platform Iframes/Widgets */}
          <div className="w-full">
            <div className="flex flex-row flex-nowrap items-center justify-center gap-1 overflow-visible sm:gap-2 md:gap-3 lg:justify-end">
              {getDefaultTrustLogos(manifest).map((logo, index) => {
                let logoMarkup;

                if ('type' in logo && logo.type === 'trustpilot') {
                  logoMarkup = (
                    <div className="flex h-[110px] w-[110px] shrink-0 origin-center scale-[0.75] items-center justify-center md:w-[220px] md:scale-100 lg:w-[155px] lg:scale-[0.75] xl:w-[200px] xl:scale-[0.9]">
                      <ClinicTrustPilotWidget scale={logo.scale} />
                    </div>
                  );
                } else if ('type' in logo && logo.type === 'doctify') {
                  logoMarkup = (
                    <div className="relative flex h-[110px] w-[105px] shrink-0 origin-center -translate-y-1 scale-[0.65] items-center justify-center md:w-[220px] md:-translate-y-2 md:scale-[0.8] lg:w-[155px] lg:-translate-y-1 lg:scale-[0.65] xl:w-[200px] xl:-translate-y-2 xl:scale-[0.75]">
                      <ClinicDoctifyWidget
                        scale={logo.scale}
                        widgetId="rate-your-experience-doctify-widget"
                      />
                    </div>
                  );
                } else {
                  logoMarkup = (
                    <div className="flex h-[110px] w-[100px] shrink-0 origin-center scale-[0.95] items-center justify-center md:w-[220px] md:scale-100 lg:w-[155px] lg:scale-[0.75] xl:w-[200px] xl:scale-[0.9]">
                      <a
                        href={logo.href || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-full w-full items-center justify-center"
                        data-testid="rich-text-logo"
                      >
                        <Image
                          src="/images/top-doctors-logo.png"
                          alt="Top Doctors"
                          width={175}
                          height={31}
                          sizes="(max-width: 768px) 100px, (max-width: 1024px) 155px, 200px"
                          className="h-auto max-h-[48px] w-auto max-w-full object-contain md:max-h-[56px]"
                        />
                      </a>
                    </div>
                  );
                }

                return (
                  <div
                    key={`${logo.type || 'logo'}-${index}`}
                    className={childClasses()}
                    style={getChildStyle(600 + index * 100)}
                  >
                    {logoMarkup}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(RateYourExperienceSection);
